import { 
  collection, 
  doc, 
  setDoc, 
  updateDoc, 
  getDoc, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';

class ContactService {
  
  /**
   * Creates a new contact in Firestore
   * @param {string} uid - User ID
   * @param {object} contactData - Contact data
   * @param {File} [imageFile] - Optional image file to upload
   * @returns {Promise<string>} - The new contact ID
   */
  async createContact(uid, contactData, imageFile = null) {
    try {
      const newContactRef = doc(collection(db, 'contacts'));
      const contactId = newContactRef.id;
      
      let imageURL = '';
      if (imageFile) {
        imageURL = await this.uploadContactImage(uid, contactId, imageFile);
      }

      const finalData = {
        id: contactId,
        uid: uid,
        ...contactData,
        imageURL: imageURL,
        isPendingtoDelete: false,
        creationDate: serverTimestamp(),
        lastUpdatedDate: serverTimestamp(),
        notesCreated: 0,
        lastNoteCreatedDate: null
      };

      await setDoc(newContactRef, finalData);
      return contactId;
    } catch (error) {
      console.error('Error creating contact:', error);
      throw error;
    }
  }

  /**
   * Updates an existing contact
   * @param {string} uid - User ID (for image path)
   * @param {string} contactId - Contact ID
   * @param {object} updateData - Data to update
   * @param {File} [newImageFile] - Optional new image file
   * @param {boolean} [removeImage] - Whether to remove the existing image
   * @returns {Promise<void>}
   */
  async updateContact(uid, contactId, updateData, newImageFile = null, removeImage = false) {
    try {
      const contactRef = doc(db, 'contacts', contactId);
      let imageUpdate = {};

      if (removeImage) {
        // If explicitly removing image
        await this.deleteContactImage(uid, contactId);
        imageUpdate = { imageURL: '' };
      } else if (newImageFile) {
        // If uploading a new image
        const imageURL = await this.uploadContactImage(uid, contactId, newImageFile);
        imageUpdate = { imageURL };
      }

      const finalUpdate = {
        ...updateData,
        ...imageUpdate,
        lastUpdatedDate: serverTimestamp()
      };

      await updateDoc(contactRef, finalUpdate);
    } catch (error) {
      console.error('Error updating contact:', error);
      throw error;
    }
  }

  /**
   * Soft deletes a contact
   * @param {string} contactId 
   * @returns {Promise<void>}
   */
  async deleteContact(contactId) {
    try {
      const contactRef = doc(db, 'contacts', contactId);
      await updateDoc(contactRef, {
        isPendingtoDelete: true,
        lastUpdatedDate: serverTimestamp()
      });
    } catch (error) {
      console.error('Error deleting contact:', error);
      throw error;
    }
  }

  /**
   * Gets a single contact by ID
   * @param {string} contactId 
   * @returns {Promise<object|null>}
   */
  async getContact(contactId) {
    try {
      const contactRef = doc(db, 'contacts', contactId);
      const contactSnap = await getDoc(contactRef);
      
      if (contactSnap.exists() && !contactSnap.data().isPendingtoDelete) {
        return contactSnap.data();
      }
      return null;
    } catch (error) {
      console.error('Error getting contact:', error);
      throw error;
    }
  }

  /**
   * Subscribes to the contacts list for a user
   * @param {string} uid - User ID
   * @param {function} onUpdate - Callback function
   * @param {object} filters - Optional filters { type, search }
   * @returns {function} - Unsubscribe function
   */
  subscribeToContacts(uid, onUpdate, filters = {}) {
    const contactsRef = collection(db, 'contacts');
    let constraints = [
      where('uid', '==', uid),
      where('isPendingtoDelete', '==', false),
      orderBy('lastUpdatedDate', 'desc')
    ];

    // Client-side filtering is often better for complex combinations (like search + type)
    // but we can filter by type if needed directly in Firestore if index exists
    if (filters.type && filters.type !== 'all') {
       constraints.push(where('type', '==', filters.type));
    }

    // Note: Search usually requires full-text search service (like Algolia) or client-side filtering.
    // We'll use client-side filtering in the component for 'search', 
    // but we can support 'type' here if we add the index: uid + isPendingtoDelete + type + name

    const q = query(contactsRef, ...constraints);

    return onSnapshot(q, (snapshot) => {
      const contacts = snapshot.docs.map(doc => doc.data());
      onUpdate(contacts);
    }, (error) => {
      console.error('Error subscribing to contacts:', error);
    });
  }

  /**
   * Subscribes to a single contact
   * @param {string} contactId 
   * @param {function} onUpdate 
   * @returns {function} - Unsubscribe function
   */
  subscribeToContact(contactId, onUpdate) {
    const contactRef = doc(db, 'contacts', contactId);
    return onSnapshot(contactRef, (doc) => {
      if (doc.exists() && !doc.data().isPendingtoDelete) {
        onUpdate(doc.data());
      } else {
        onUpdate(null);
      }
    }, (error) => {
      console.error('Error subscribing to contact:', error);
    });
  }

  /**
   * Uploads a contact image to Firebase Storage
   * @param {string} uid 
   * @param {string} contactId 
   * @param {File} file 
   * @returns {Promise<string>} - Download URL
   */
  async uploadContactImage(uid, contactId, file) {
    try {
      // Path: contactImages/{uid}/{contactId}.jpg
      // We use .jpg for simplicity, or we could preserve extension
      const storageRef = ref(storage, `contactImages/${uid}/${contactId}.jpg`);
      
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return downloadURL;
    } catch (error) {
      console.error('Error uploading contact image:', error);
      throw error;
    }
  }

  /**
   * Deletes a contact image from Firebase Storage
   * @param {string} uid 
   * @param {string} contactId 
   */
  async deleteContactImage(uid, contactId) {
    try {
      const storageRef = ref(storage, `contactImages/${uid}/${contactId}.jpg`);
      await deleteObject(storageRef);
    } catch (error) {
      // Ignore if object not found
      if (error.code !== 'storage/object-not-found') {
        console.error('Error deleting contact image:', error);
      }
    }
  }

  /**
   * Fetch persons related to an organization
   * @param {string} uid 
   * @param {string} organizationId 
   * @param {function} onUpdate 
   * @returns {function}
   */
  subscribeToOrganizationPeople(uid, organizationId, onUpdate) {
    const contactsRef = collection(db, 'contacts');
    const q = query(
      contactsRef,
      where('uid', '==', uid),
      where('isPendingtoDelete', '==', false),
      where('type', '==', 'person'),
      where('relatedAccountId', '==', organizationId),
      orderBy('name', 'asc')
    );

    return onSnapshot(q, (snapshot) => {
      const people = snapshot.docs.map(doc => doc.data());
      onUpdate(people);
    });
  }
  
  /**
   * Get organizations list for dropdowns (filtered by uid)
   * @param {string} uid 
   * @returns {Promise<Array>}
   */
  async getOrganizations(uid) {
      const contactsRef = collection(db, 'contacts');
      const q = query(
          contactsRef,
          where('uid', '==', uid),
          where('isPendingtoDelete', '==', false),
          where('type', '==', 'organization'),
          orderBy('name', 'asc')
      );
      // We use getDocs instead of onSnapshot for one-time fetch (like in dropdowns)
      // but actually import getDocs is needed.
      // Let's use a one-time promise wrapper around onSnapshot or import getDocs
      // Ideally I should import getDocs at the top.
      
      // Lazy import or assuming getDocs was imported.
      // I added getDocs to import list but I see I missed it in the actual file content above.
      // I need to fix imports first.
      
      return new Promise((resolve, reject) => {
          const unsubscribe = onSnapshot(q, (snapshot) => {
              const orgs = snapshot.docs.map(doc => doc.data());
              unsubscribe();
              resolve(orgs);
          }, reject);
      });
  }

  /**
   * Subscribes to notes associated with a contact
   * @param {string} uid 
   * @param {string} contactId 
   * @param {function} onUpdate 
   * @returns {function}
   */
  subscribeToContactNotes(uid, contactId, onUpdate) {
    const notesRef = collection(db, 'notes');
    const q = query(
      notesRef,
      where('uid', '==', uid),
      where('isPendingtoDelete', '==', false), // CRITICAL: Filter soft-deleted notes
      where('contactIds', 'array-contains', contactId),
      orderBy('creationDate', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
      const notes = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
      }));
      onUpdate(notes);
    }, (error) => {
      console.error('Error subscribing to contact notes:', error);
    });
  }
}

export const contactService = new ContactService();

